import { useState, useEffect, useRef, useCallback } from 'react';
import { bffFetch } from '@zehnmindai/embed/bff';
import i18n from '../../i18n';
import { parseApiError } from '../utils/apiError.js';
import { WOPI_TOKEN, WOPI_TOKEN_FROM_SHARE, WOPI_DISCOVERY } from '../utils/urls.js';
import { env } from '@/shared/utils/env';

// docs-api is on this page's own origin. A person's WOPI token is asked for
// with docs' session cookie (bffFetch: the cookie, and X-Zehn-CSRF on the
// POST); a guest link's token is asked for with the link's share token alone
// (a capability: no cookie goes along), and discovery needs nobody.

const fallbackCollaboraUrl = () =>
  env('VITE_COLLABORA_URL') || `${window.location.protocol}//collabora.${window.location.hostname}`;

/** The token request: a person's (by the file's uuid) or a guest link's. */
function requestToken({ fileUuid, shareToken, readonly, version, signal }) {
  if (shareToken) {
    // A file inside a folder link names itself; the backend opens only what
    // the link reaches.
    const params = new URLSearchParams({ share_token: shareToken });
    if (fileUuid) params.set('file_uuid', String(fileUuid));
    return fetch(`${WOPI_TOKEN_FROM_SHARE}?${params}`, { method: 'POST', credentials: 'omit', signal });
  }
  const params = new URLSearchParams();
  if (readonly) params.set('readonly', 'true');
  if (version != null) params.set('version', String(version));
  const qs = params.toString();
  const path = WOPI_TOKEN.replace('{file_uuid}', encodeURIComponent(String(fileUuid)));
  return bffFetch(`${path}${qs ? `?${qs}` : ''}`, { method: 'POST', signal });
}

/**
 * Hook to fetch and manage WOPI access tokens for Collabora Online.
 *
 * @param {string} fileUuid - The file's uuid (with a shareToken: a file inside the linked folder, or none for the linked file)
 * @param {object} options
 * @param {boolean} options.enabled - Whether to fetch the token (default: true)
 * @param {boolean} options.readonly - Force readonly mode
 * @param {string}  options.shareToken - If provided, exchange a guest share token for a read-only WOPI token
 * @param {number}  options.version - Read-only preview of a historical file version
 * @returns {{ token, canWrite, wopiSrc, collaboraUrl, loading, error, errorInfo, refresh }}
 *   canWrite — the token's `can_write` (false: Collabora opens read-only)
 *   errorInfo — {status, code} of a refused token request (status null for a
 *   network failure or timeout), so a caller can tell "no access" from a fault.
 */
export function useWopiToken(
  fileUuid,
  { enabled = true, readonly = false, shareToken = null, version = null } = {},
) {
  const [token, setToken] = useState(null);
  // Whether the session may save (`can_write`: editor level, the file not
  // locked, signed, under agreement or filed, and `files:update`), or null
  // before the first token.
  const [canWrite, setCanWrite] = useState(null);
  const [tokenTtl, setTokenTtl] = useState(null); // seconds
  const [wopiSrc, setWopiSrc] = useState(null);
  const [collaboraUrl, setCollaboaUrl] = useState(null);
  const [editorPath, setEditorPath] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorInfo, setErrorInfo] = useState(null);
  const refreshTimerRef = useRef(null);
  // Bumped whenever the editor moves on (another file, or gone): a request
  // still in flight then applies nothing and schedules no renewal.
  const generationRef = useRef(0);

  // A renewal (`renew`) runs while the editor is open on the current token:
  // it shows no spinner, and a failure keeps the editor and that token and
  // tries again in a minute instead of replacing the editor with an error.
  const fetchToken = useCallback(async ({ renew = false } = {}) => {
    if (!enabled) return;
    if (!fileUuid && !shareToken) return;

    const generation = generationRef.current;
    const current = () => generationRef.current === generation;
    const scheduleRenewal = (ms) => {
      if (!current()) return;
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = setTimeout(() => fetchToken({ renew: true }), ms);
    };

    if (!renew) {
      setLoading(true);
      setError(null);
      setErrorInfo(null);
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);

    try {
      const resp = await requestToken({ fileUuid, shareToken, readonly, version, signal: controller.signal });

      clearTimeout(timeoutId);

      if (!resp.ok) {
        const body = await resp.json().catch(() => null);
        const parsed = parseApiError({ status: resp.status, data: body });
        throw Object.assign(new Error(`Failed to get WOPI token: ${resp.status}`), {
          info: { status: resp.status, code: parsed.code || null },
        });
      }

      const data = await resp.json();
      if (!current()) return;
      setToken(data.access_token);
      setCanWrite(Boolean(data.can_write));
      setWopiSrc(data.wopi_src);
      setTokenTtl(data.access_token_ttl || null);

      // Renew at half the token's life (an hour at most): well before
      // Collabora's own expiry warning, 15 minutes before the end. The editor
      // takes the new token in place, without a reload.
      if (data.access_token_ttl) {
        scheduleRenewal(data.access_token_ttl * 500);
      }
    } catch (err) {
      clearTimeout(timeoutId);
      if (!current()) return;
      if (renew) {
        console.warn('[useWopiToken] Renewal failed, retrying in a minute:', err);
        scheduleRenewal(60_000);
        return;
      }
      console.error('[useWopiToken] Error:', err);
      setError(err.name === 'AbortError' ? i18n.t('editor:requestTimeout') : err.message);
      setErrorInfo(err.info || { status: null, code: null });
    } finally {
      if (!renew && current()) setLoading(false);
    }
  }, [fileUuid, enabled, readonly, shareToken, version]);

  // Fetch Collabora editor URL from discovery
  useEffect(() => {
    if (!enabled) return;

    const fetchDiscovery = async () => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      try {
        const resp = await fetch(WOPI_DISCOVERY, { credentials: 'omit', signal: controller.signal });
        clearTimeout(timeoutId);

        if (!resp.ok) {
          console.warn('[useWopiToken] Discovery fetch failed, using fallback URL');
          // Fallback: construct URL from env or default
          setCollaboaUrl(fallbackCollaboraUrl());
          return;
        }

        const xml = await resp.text();
        // We only need the COOL base URL — every file extension routes through
        // the same `/browser/dist/cool.html`. Collabora picks the right app
        // (Writer / Calc / Impress) from the BaseFileName our WOPI metadata
        // returns. So grab any edit action; docx is just a likely-present hint.
        const parser = new DOMParser();
        const doc = parser.parseFromString(xml, 'application/xml');
        const actions = doc.querySelectorAll('action');
        let editorUrl = null;

        for (const action of actions) {
          const ext = action.getAttribute('ext');
          const name = action.getAttribute('name');
          if (ext === 'docx' && name === 'edit') {
            editorUrl = action.getAttribute('urlsrc');
            break;
          }
        }

        if (!editorUrl) {
          for (const action of actions) {
            if (action.getAttribute('name') === 'edit') {
              editorUrl = action.getAttribute('urlsrc');
              break;
            }
          }
        }

        if (editorUrl) {
          // Extract base URL (remove query params template)
          const baseUrl = editorUrl.split('?')[0];
          // Extract the Collabora host from the URL
          const urlObj = new URL(baseUrl);
          setCollaboaUrl(urlObj.origin);
          setEditorPath(urlObj.pathname);
        } else {
          setCollaboaUrl(fallbackCollaboraUrl());
        }
      } catch (err) {
        clearTimeout(timeoutId);
        console.warn('[useWopiToken] Discovery error, using fallback:', err);
        setCollaboaUrl(fallbackCollaboraUrl());
      }
    };

    fetchDiscovery();
  }, [enabled]);

  // Fetch token when the file changes
  useEffect(() => {
    fetchToken();
    return () => {
      generationRef.current += 1;
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [fetchToken]);

  return {
    token,
    canWrite,
    tokenTtl,
    wopiSrc,
    collaboraUrl,
    editorPath,
    loading,
    error,
    errorInfo,
    refresh: () => fetchToken(),
  };
}
